/** Thin typed layer over Telegram.WebApp that degrades to no-ops in a plain browser. */

type Haptic = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type Notice = 'success' | 'error' | 'warning';

interface TgButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  isVisible: boolean;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name: string; photo_url?: string } };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  isVersionAtLeast(v: string): boolean;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(c: string): void;
  setBackgroundColor(c: string): void;
  setBottomBarColor?(c: string): void;
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  onEvent(e: string, cb: (...args: unknown[]) => void): void;
  offEvent(e: string, cb: (...args: unknown[]) => void): void;
  BackButton: TgButton;
  HapticFeedback: {
    impactOccurred(s: Haptic): void;
    notificationOccurred(t: Notice): void;
    selectionChanged(): void;
  };
  openLink(url: string): void;
  openTelegramLink(url: string): void;
  switchInlineQuery(query: string, chooseChatTypes?: Array<'users' | 'bots' | 'groups' | 'channels'>): void;
  showScanQrPopup(params: { text?: string }, cb: (text: string) => boolean | void): void;
  closeScanQrPopup(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

const raw = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
/** The script is present even in a browser; real Mini App sessions carry signed initData. */
export const tg: TgWebApp | null = raw && raw.initData ? raw : null;
export const inTelegram = !!tg;

const atLeast = (v: string) => !!tg && tg.isVersionAtLeast(v);

export function bootTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
  if (atLeast('7.7')) tg.disableVerticalSwipes?.();
}

export function paintChrome(ground: string): void {
  if (!tg || !atLeast('6.1')) return;
  tg.setHeaderColor(ground);
  tg.setBackgroundColor(ground);
  if (atLeast('7.10')) tg.setBottomBarColor?.(ground);
}

export const haptic = {
  tap: () => tg && atLeast('6.1') && tg.HapticFeedback.impactOccurred('light'),
  press: () => tg && atLeast('6.1') && tg.HapticFeedback.impactOccurred('medium'),
  select: () => tg && atLeast('6.1') && tg.HapticFeedback.selectionChanged(),
  success: () => tg && atLeast('6.1') && tg.HapticFeedback.notificationOccurred('success'),
  error: () => tg && atLeast('6.1') && tg.HapticFeedback.notificationOccurred('error'),
  warn: () => tg && atLeast('6.1') && tg.HapticFeedback.notificationOccurred('warning'),
};

export function startParam(): string | null {
  return tg?.initDataUnsafe.start_param ?? null;
}

export function onThemeChange(cb: (scheme: 'light' | 'dark') => void): () => void {
  if (!tg) return () => {};
  const handler = () => cb(tg.colorScheme);
  tg.onEvent('themeChanged', handler);
  return () => tg.offEvent('themeChanged', handler);
}

export function telegramScheme(): 'light' | 'dark' | null {
  return tg ? tg.colorScheme : null;
}

export function backButton(visible: boolean, onBack: () => void): () => void {
  if (!tg || !atLeast('6.1')) return () => {};
  if (!visible) {
    tg.BackButton.hide();
    return () => {};
  }
  tg.BackButton.show();
  tg.BackButton.onClick(onBack);
  return () => tg.BackButton.offClick(onBack);
}

export const canScanQr = () => atLeast('6.4');

export function scanQr(text: string): Promise<string | null> {
  return new Promise((resolve) => {
    if (!tg || !canScanQr()) return resolve(null);
    let done = false;
    tg.showScanQrPopup({ text }, (value) => {
      if (done) return true;
      done = true;
      resolve(value);
      return true;
    });
    const onClosed = () => {
      tg.offEvent('scanQrPopupClosed', onClosed);
      if (!done) resolve(null);
    };
    tg.onEvent('scanQrPopupClosed', onClosed);
  });
}

/** Share a cheque or invoice into any chat through the bot's inline mode. */
export function shareInline(query: string, fallbackUrl: string): void {
  if (tg && atLeast('6.7')) {
    tg.switchInlineQuery(query, ['users', 'groups', 'channels']);
    return;
  }
  if (navigator.share) {
    void navigator.share({ url: fallbackUrl }).catch(() => {});
    return;
  }
  void copyText(fallbackUrl);
}

export function openExternal(url: string): void {
  if (tg) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}

export async function pasteText(): Promise<string | null> {
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}
